import { when } from 'mobx';

import type { DonationForm, DonationForms } from '@feathr/blackbox';

/**
 * Extended settings interface that includes defaultStyling.
 * The golden-goose editor adds this field to config.settings.
 */
interface IExtendedSettings {
  defaultStyling?: boolean;
  [key: string]: unknown;
}

interface IExtendedFormConfig {
  settings?: IExtendedSettings;
  [key: string]: unknown;
}

/**
 * Clears the defaultStyling flag on all donation forms except the specified one.
 * This ensures only one form can be the default at a time.
 *
 * @param DonationForms - The DonationForms collection from the store
 * @param excludeFormId - The ID of the form to exclude from clearing (the current form being saved)
 * @returns Promise that resolves when all forms have been updated
 *
 * @example
 * await clearDefaultStylingOnOtherForms(DonationForms, currentForm.id);
 */
export async function clearDefaultStylingOnOtherForms(
  DonationForms: DonationForms,
  excludeFormId: string,
): Promise<void> {
  // Use a large page size to ensure we check all forms in the account
  // TODO: Consider implementing pagination if accounts can have more than 1000 forms
  const formsList = DonationForms.list({ pagination: { page_size: 1000 } });
  await when(() => !formsList.isPending);

  const formsToUpdate = formsList.models.filter((form) => {
    if (form.id === excludeFormId) {
      return false;
    }
    const config = form.get('config') as unknown as IExtendedFormConfig | undefined;
    return config?.settings?.defaultStyling === true;
  });

  await Promise.all(
    formsToUpdate.map(async (form) => {
      const config = form.get('config') as unknown as IExtendedFormConfig;
      const updatedConfig = {
        ...config,
        settings: {
          ...config.settings,
          defaultStyling: false,
        },
      };
      form.set({ config: updatedConfig as unknown as DonationForm['config'] });
      await form.patchDirty();
    }),
  );
}
